import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Send } from 'lucide-react';
import { useToast } from '@/context/ToastContext';
import { ccySym, normalizeCurrencies } from '@/utils/currencyUtils';
import SenderFeeSummary from '@/components/SenderFeeSummary';
import { usePropositionDraft } from '@/hooks/usePropositionDraft';
import CurrencySelector from '@/components/currency/CurrencySelector';
import { BACKEND_URL } from '@/config';

const API = BACKEND_URL + '/api';
const WINE = '#722f37';
const DIFF_COLOR = { 'dp-diff--under': '#075044', 'dp-diff--over': '#dc2626', 'dp-diff--eq': '#6B7280' };

/**
 * Formulaire de proposition déplié sous une carte de matching (ShipmentMatchingPage, TripMatchingPage).
 * `target` : l'annonce de la carte · `mine` : mon annonce (celle de la page) · `mode` : 'direct' | 'negotiation'.
 * Prix, devise, garde-fous et requête viennent du brouillon commun (`usePropositionDraft`).
 */
export default function MatchPropositionForm({ meSide, target, mine, mode, theirFirstName }) {
  const navigate = useNavigate();
  const toast = useToast();
  const draft = usePropositionDraft({ meSide, target, mine });
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isNegotiation = mode === 'negotiation';
  const sym = ccySym(draft.currency || target.primary_currency || 'EUR');
  const targetLabel = meSide === 'sender' ? 'ce trajet' : 'ce colis';

  const send = async () => {
    if (!draft.canSubmit(mode)) return;
    setSubmitting(true);
    try {
      const payload = draft.payload(mode);
      if (message.trim()) payload.message = message.trim();
      const r = await axios.post(`${API}/propositions`, payload);

      if (isNegotiation && target.user_id) {
        try {
          const dm = await axios.post(`${API}/conversations/direct`, { user_id: target.user_id });
          const tp = draft.targetPrice;
          const reference = meSide === 'sender'
            ? (tp ? `Tarif du trajet : ${tp.amount} ${ccySym(tp.currency)}` : 'Tarif libre')
            : (tp ? `Budget du colis : ${tp.amount} ${ccySym(tp.currency)}` : 'Budget libre');
          const offerMsg = [
            `💬 Contre-offre : ${draft.price} ${sym} pour ${target.departure_city} → ${target.arrival_city}`,
            reference,
            message.trim() ? `Message : ${message.trim()}` : null,
            `→ Réf. proposition #${r.data?.id?.slice(-6) || '...'}`,
          ].filter(Boolean).join('\n');
          await axios.post(`${API}/conversations/${dm.data.id}/messages`, { content: offerMsg });
        } catch { /* non bloquant */ }
      }

      toast.success(isNegotiation ? 'Contre-offre envoyée ✨' : 'Proposition envoyée ✨');
      navigate('/app/propositions');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 409 && detail.includes('déjà une proposition')) {
        toast.error(`Vous avez déjà une proposition en attente pour ${targetLabel}. Retirez-la depuis Mes propositions avant d'en créer une nouvelle.`);
      } else if (status === 409 && detail.includes('mission')) {
        toast.error('Une mission est déjà en cours pour ces annonces.');
      } else {
        toast.error(typeof detail === 'string' && detail ? detail : "Erreur lors de l'envoi.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-prop-form">
      {isNegotiation && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fdf2f4', border: `1px solid ${WINE}33`, borderRadius: 7, marginBottom: 10, fontSize: 12 }}>
          <span>💬</span><span style={{ color: WINE, fontWeight: 600 }}>Mode Négociation</span>
          <span style={{ color: '#6b7280' }}>Prix modifiable</span>
        </div>
      )}
      <CurrencySelector
        myCurrencies={normalizeCurrencies(mine)}
        theirCurrencies={normalizeCurrencies(target)}
        theirFirstName={theirFirstName}
        theirPrimary={target.primary_currency}
        amountCcy={target.primary_currency || 'EUR'}
        amount={draft.targetPrice?.amount || 0}
        value={draft.currency}
        onChange={draft.setCurrency}
        onConvert={draft.onTargetConverted}
      />
      <div className="mt-prop-row">
        <div className="mt-prop-field">
          <label htmlFor={`mt-price-${target.id}`}>
            Prix proposé ({sym})
            {!isNegotiation && <span style={{ marginLeft: 5, fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>(verrouillé)</span>}
          </label>
          <input
            id={`mt-price-${target.id}`}
            type="number" min="0" step="1"
            value={isNegotiation ? draft.price : (draft.directPrice ?? '')}
            onChange={e => isNegotiation && draft.setPrice(e.target.value)}
            disabled={!isNegotiation}
            style={isNegotiation ? { borderColor: WINE, boxShadow: `0 0 0 2px ${WINE}22` } : { background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' }}
            data-testid={`mt-price-input-${target.id}`}
          />
          {draft.floorNote(mode) && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#b45309' }} data-testid={`mt-price-floor-${target.id}`}>
              {draft.floorNote(mode)}
            </div>
          )}
          {!isNegotiation && draft.conversionMissing && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#b45309' }} data-testid={`mt-conversion-missing-${target.id}`}>
              Conversion indisponible pour le moment : choisissez la devise de l'annonce, ou passez en négociation.
            </div>
          )}
          {isNegotiation && draft.diff && (
            <div style={{ marginTop: 6, fontSize: 12, color: DIFF_COLOR[draft.diff.cls] }} data-testid={`mt-price-diff-${target.id}`}>
              {draft.diff.label}
            </div>
          )}
          {meSide === 'sender' && <SenderFeeSummary fee={draft.senderFee(mode)} totalLabel="Total à payer s'il accepte" withTotal />}
        </div>
        <div className="mt-prop-field mt-prop-field-msg">
          <label htmlFor={`mt-msg-${target.id}`}>{isNegotiation ? 'Contre-offre (message)' : 'Message (optionnel)'}</label>
          <input
            id={`mt-msg-${target.id}`}
            type="text" maxLength={300}
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder={isNegotiation
              ? 'Expliquez votre contre-offre…'
              : (meSide === 'sender' ? 'Bonjour, je souhaite que vous transportiez mon colis…' : 'Bonjour, je peux transporter votre colis…')}
            data-testid={`mt-msg-input-${target.id}`}
          />
        </div>
      </div>
      <button
        className="mt-btn mt-btn-primary mt-btn-block"
        disabled={submitting || !draft.canSubmit(mode)}
        onClick={send}
        style={isNegotiation ? { background: WINE, borderColor: WINE } : undefined}
        data-testid={`mt-confirm-propose-${target.id}`}
      >
        {submitting ? 'Envoi…' : isNegotiation
          ? '💬 Envoyer la contre-offre'
          : <><Send size={14} /> Confirmer ma proposition</>}
      </button>
    </div>
  );
}
